import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, User, HelpCircle, ShoppingCart, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import WebsiteSwitcher from "../WebsiteSwitcher";
import { supabase } from "../../utils/supabase/client";
import { projectId } from "../../utils/supabase/info";
import { toast } from "sonner";
import { getValidAccessToken } from "../../utils/auth";
import { LogoIcon } from "../../assets";
//import { useWebsite } from "@/contexts/WebsiteContext";

export default function TopNav() {
  const navigate = useNavigate();
  const [userInitials, setUserInitials] = useState("JD");
  const [userName, setUserName] = useState("");
  const [userPhotoUrl, setUserPhotoUrl] = useState("");
  const [isAddSiteOpen, setIsAddSiteOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [website_name, setWebsite_name] = useState("");
  const [domain, setDomain] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  //const { websites, selectedWebsite, setSelectedWebsite } = useWebsite();

  

  const updateProfileDisplay = useCallback((profile: any) => {
    // Safely calculate initials with fallback
    const firstName = profile.first_name || "";
    const lastName = profile.last_name || "";

    if (firstName && lastName) {
      const initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
      setUserInitials(initials);
      setUserName(`${firstName} ${lastName}`);
    } else if (firstName) {
      setUserInitials(firstName.substring(0, 2).toUpperCase());
      setUserName(firstName);
    } else if (profile.email) {
      setUserInitials(profile.email.substring(0, 2).toUpperCase());
      setUserName(profile.email);
    }

    if (profile.profile_photo_url) {
      setUserPhotoUrl(profile.profile_photo_url);
    }
  }, []);

  const handleProfilePhotoUpdate = useCallback((event: CustomEvent) => {
    setUserPhotoUrl(event.detail.photoUrl);
  }, []);

  const handleOpenAddWebsite = useCallback(() => {
    setIsAddSiteOpen(true);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchUserProfile = async () => {
      try {
        // Check for cached profile first
        const cachedProfile = localStorage.getItem("userProfile");
        const timestampStr = localStorage.getItem("userProfileTimestamp");
        const now = Date.now();
        let shouldFetch = true;

        if (cachedProfile && timestampStr) {
          const timestamp = parseInt(timestampStr);
          if (now - timestamp < 5 * 60 * 1000) { // 5 minutes cache validity
            try {
              const profile = JSON.parse(cachedProfile);
              updateProfileDisplay(profile);
              shouldFetch = false;
            } catch (e) {
            }
          }
        }

        if (!shouldFetch) {
          return;
        }

        const accessToken = await getValidAccessToken();
        if (!accessToken) {
          return;
        }

        if (!isMounted) {
          return;
        }


        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);

        try {
          const response = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-3b83895f/profile`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
              signal: controller.signal,
            }
          );

          clearTimeout(timeoutId);

          if (!isMounted) {
            return;
          }

          if (response.ok) {
            const data = await response.json();

            if (data.profile) {
              updateProfileDisplay(data.profile);
              // Update cache
              localStorage.setItem("userProfile", JSON.stringify(data.profile));
              localStorage.setItem(
                "userProfileTimestamp",
                Date.now().toString()
              );
            }
          } else {
          }
        } catch (fetchError: any) {
          clearTimeout(timeoutId);
          if (fetchError.name !== "AbortError") {
          }
        }
      } catch (error) {
      }
    };

    // Fetch profile on mount
    fetchUserProfile();

    // Listen for profile photo updates from Settings page
    window.addEventListener(
      "profilePhotoUpdated",
      handleProfilePhotoUpdate as EventListener
    );
    // Listen for request to open Add Website dialog
    window.addEventListener("openAddWebsiteDialog", handleOpenAddWebsite);

    // Cleanup listener on unmount
    return () => {
      isMounted = false;
      window.removeEventListener(
        "profilePhotoUpdated",
        handleProfilePhotoUpdate as EventListener
      );
      window.removeEventListener("openAddWebsiteDialog", handleOpenAddWebsite);
    };
  }, [updateProfileDisplay, handleProfilePhotoUpdate, handleOpenAddWebsite]);

  const handleLogout = useCallback(async () => {

    // Clear localStorage immediately
    localStorage.clear();
    sessionStorage.clear();

    // Show success message
    toast.success("Logged out successfully");

    // Navigate immediately - don't wait for async operations
    navigate("/login", { replace: true });

    // Clear Supabase session in background (don't await)
    supabase.auth.signOut().catch((err) => {
    });
  }, [navigate]);

  const openAddSiteDialog = useCallback(() => {
    setIsAddSiteOpen(true);
  }, []);

  const openSupportDialog = useCallback(() => {
    setIsSupportOpen(true);
  }, []);

  const goToSettings = useCallback(() => {
    navigate("/settings");
  }, [navigate]);

/*   const goToOrders = useCallback(() => {
    navigate("/manage-plan");
  }, [navigate]); */

  const handleEmailSupport = useCallback(() => {
    window.location.href =
      "mailto:support@axorigin.org?subject=Support Request from AxOrigin Dashboard";
    setIsSupportOpen(false);
  }, []);

  const handleAddSite = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);

      try {
        // Get valid access token
        const accessToken = await getValidAccessToken();

        if (!accessToken) {
          toast.error("Your session has expired. Please login again.");
          setIsSubmitting(false);
          navigate("/login");
          return;
        }

        const domainURL = new URL(domain);
        const hostname = domainURL.hostname;

        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-3b83895f/websites`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              website_name: website_name,
              domain: hostname,
               action: "upsert"
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          toast.success("Website added successfully!");
          setIsAddSiteOpen(false);
          setWebsite_name("");
          setDomain("");

          // Dispatch custom event to notify WebsiteSwitcher to refresh
          window.dispatchEvent(new Event("websiteAdded"));

          // ✅ Redirect to manage plan using newly created website ID
          if (data.website?.id) {
            navigate(`/manage-plan/${data.website.id}`);
          } else {
            navigate("/dashboard");
          }
        } else {
          const errorData = await response
            .json()
            .catch(() => ({ error: "Unknown error" }));
          toast.error(
            errorData.error || errorData.message || "Failed to add website"
          );
        }
      } catch (error) {
        toast.error("Failed to add website. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [domain, website_name, navigate]
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center gap-4 px-4 md:px-6">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="mx-auto  flex h-12 w-12 items-center justify-center rounded-lg">
            <LogoIcon className="w-10 h-10" />
          </div>
          <span className="hidden md:inline-block">AxOrigin</span>
        </div>

        {/* Website Switcher */}
        <div className="max-w-md">
          <WebsiteSwitcher />
        </div>

        {/* Spacer to push right items to the end */}
        <div className="flex-1"></div>

        {/* Right side group: Add Site, Email Verification, Language, Profile */}
        <div className="flex items-center gap-3">
          {/* Add New Site Button */}
          <Button
            className="hidden md:flex bg-primary-gradient"
            onClick={openAddSiteDialog}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add New Site
          </Button>

          {/* Email Verification Badge */}
          {/* <div className="hidden sm:block">
            <EmailVerificationBadge />
          </div> */}

          {/* Language Switcher */}
          {/* <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="w-[120px] hidden lg:flex">
              <Globe className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="de">Deutsch</SelectItem>
            </SelectContent>
          </Select> */}

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="relative h-10 w-10 rounded-full hover:opacity-80 transition-opacity">
                <Avatar>
                  <AvatarImage src={userPhotoUrl} alt={userName} />
                  <AvatarFallback className="bg-primary-gradient text-white">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel>{userName || "My Account"}</DropdownMenuLabel>

              {/* Email Verification Badge in Dropdown (for mobile) */}
              {/* <div className="px-2 py-1.5 sm:hidden">
                <EmailVerificationBadge />
              </div> */}

              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={goToSettings}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openSupportDialog}>
                <HelpCircle className="mr-2 h-4 w-4" />
                Support
              </DropdownMenuItem>
              {/* <DropdownMenuItem onClick={goToOrders}>
                <ShoppingCart className="mr-2 h-4 w-4" />
                Orders
              </DropdownMenuItem> */}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Add New Site Dialog */}
      <Dialog open={isAddSiteOpen} onOpenChange={setIsAddSiteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Website</DialogTitle>
            <DialogDescription>
              Enter your website details to start monitoring accessibility
              compliance.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSite}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="website_name">Website Name</Label>
                <Input
                  id="website_name"
                  placeholder="Enter website name"
                  value={website_name}
                  onChange={(e) => setWebsite_name(e.target.value)}
                  required
                  className="bg-input-background border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="domain">Website URL</Label>
                <Input
                  id="domain"
                  type="url"
                  placeholder="https://www.example.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  required
                  className="bg-input-background border-border"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddSiteOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-primary-gradient text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Adding..." : "Add Website"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Support Dialog */}
      <Dialog open={isSupportOpen} onOpenChange={setIsSupportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="rounded-full bg-primary/10 p-4">
                <HelpCircle className="h-8 w-8 text-primary" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl">
              Need Help?
            </DialogTitle>
            <DialogDescription className="text-center pt-2">
              Our support page is coming soon! In the meantime, we're here to
              help.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-3">
              <h4 className="font-medium">Contact Our Support Team</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  📧 Email:{" "}
                  <a
                    href="mailto:support@axorigin.org"
                    className="text-primary hover:underline"
                  >
                    support@axorigin.org
                  </a>
                </p>
                <p>⏰ Response Time: Within 24 hours</p>
                <p>📚 Documentation: Coming Soon</p>
              </div>
            </div>
            
          </div>
          <DialogFooter className="sm:justify-center">
            <Button
              type="button"
              className="bg-primary-gradient text-white w-full sm:w-auto"
              onClick={handleEmailSupport}
            >
              Email Support Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}